import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getCallLogsAPI, getRecordingsAPI, deleteRecordingAPI } from '../../context-api/api-services/twilio.service';
import useTwilioVoice from '../../hooks/useTwilioVoice';
import styles from './TwilioPhone.module.css';

// ─── Helpers ───────────────────────────────────────────────────────────────

const AGENT_IDENTITY = 'agent_123';

const PAGE_LIMIT = 10;

const STATUS_LABELS = {
    idle: 'Idle',
    registering: 'Registering…',
    ready: 'Ready',
    calling: 'Calling…',
    in_call: 'In Call',
    ended: 'Ended',
    error: 'Error',
};

function status_dot_class(status) {
    const map = {
        idle: styles.status_dot_idle,
        registering: styles.status_dot_registering,
        ready: styles.status_dot_ready,
        calling: styles.status_dot_calling,
        in_call: styles.status_dot_in_call,
        ended: styles.status_dot_ended,
        error: styles.status_dot_error,
    };
    return map[status] || styles.status_dot_idle;
}

function call_badge(status) {
    const map = {
        completed: styles.badge_completed,
        busy: styles.badge_busy,
        failed: styles.badge_failed,
        'no-answer': styles.badge_no_answer,
        'in-progress': styles.badge_inprogress,
    };
    return map[(status || '').toLowerCase()] || styles.badge_default;
}

function fmt_duration(seconds) {
    if (!seconds && seconds !== 0) return '—';
    const s = Number(seconds);
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${String(rem).padStart(2, '0')}`;
}

function fmt_date(raw) {
    if (!raw) return '—';
    try {
        return new Date(raw).toLocaleString();
    } catch (_) {
        return raw;
    }
}

function unwrap(response) {
    if (!response || response.hasError) return null;
    return response?.data ?? response;
}

// ─── Phone Panel ───────────────────────────────────────────────────────────

function PhonePanel({ voice }) {
    const [destination, setDestination] = useState('');
    const input_ref = useRef(null);

    const {
        status, last_error, is_muted,
        is_ready, is_calling, is_in_call,
        incoming_call,
        call, hangup, toggle_mute,
        accept_incoming, reject_incoming,
        clear_error, retry_init,
    } = voice;

    const can_call = is_ready && destination.trim().length > 0 && !is_calling && !is_in_call;
    const can_hangup = is_calling || is_in_call;

    const handle_call = useCallback(() => {
        call(destination);
    }, [call, destination]);

    const handle_key_down = useCallback((e) => {
        if (e.key === 'Enter' && can_call) handle_call();
    }, [can_call, handle_call]);

    return (
        <div className={styles.phone_panel}>
            <div className={styles.status_row}>
                <span className={`${styles.status_dot} ${status_dot_class(status)}`} />
                <span className={styles.status_label}>{STATUS_LABELS[status] || status}</span>
            </div>

            {incoming_call && (
                <div className={styles.incoming_banner}>
                    <div className={styles.incoming_label}>📞 Incoming Call</div>
                    <div className={styles.incoming_from}>
                        From: {incoming_call.parameters?.From || 'Unknown'}
                    </div>
                    <div className={styles.incoming_actions}>
                        <button className={styles.btn_accept} onClick={accept_incoming}>Accept</button>
                        <button className={styles.btn_reject} onClick={reject_incoming}>Decline</button>
                    </div>
                </div>
            )}

            {last_error && (
                <div className={styles.error_banner}>
                    <span className={styles.error_icon}>⚠️</span>
                    <span className={styles.error_text}>{last_error}</span>
                    <button className={styles.error_dismiss} onClick={clear_error}>✕</button>
                </div>
            )}

            {status === 'error' && (
                <button className={styles.btn_retry} onClick={retry_init}>
                    Retry Connection
                </button>
            )}

            <div className={styles.input_wrap}>
                <label className={styles.input_label}>Destination</label>
                <input
                    ref={input_ref}
                    className={styles.destination_input}
                    type="text"
                    placeholder="+14155551234 or client:name"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    onKeyDown={handle_key_down}
                    disabled={!is_ready && !is_in_call && !is_calling}
                />
                <span className={styles.input_hint}>E.164 number or client:identifier</span>
            </div>

            <div className={styles.btn_group}>
                <button
                    className={styles.btn_call}
                    onClick={handle_call}
                    disabled={!can_call}
                >
                    Call
                </button>
                <button
                    className={styles.btn_hangup}
                    onClick={hangup}
                    disabled={!can_hangup}
                >
                    Hang Up
                </button>
            </div>

            {(is_in_call) && (
                <button
                    className={`${styles.btn_mute} ${is_muted ? styles.btn_mute_active : ''}`}
                    onClick={toggle_mute}
                >
                    {is_muted ? '🔇 Unmute' : '🎙 Mute'}
                </button>
            )}
        </div>
    );
}

// ─── Call Logs ─────────────────────────────────────────────────────────────

function CallLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total_pages, setTotalPages] = useState(1);
    const [status_filter, setStatusFilter] = useState('');
    const [direction_filter, setDirectionFilter] = useState('');
    const poll_ref = useRef(null);

    const fetch_logs = useCallback(async (p = page) => {
        setLoading(true);
        const params = { page: p, limit: PAGE_LIMIT };
        if (status_filter) params.status = status_filter;
        if (direction_filter) params.direction = direction_filter;

        const result = await getCallLogsAPI(params);
        const data = unwrap(result);

        if (data) {
            const items = Array.isArray(data) ? data : (data.calls || data.data || data.items || []);
            setLogs(items);
            if (data.total_pages) setTotalPages(data.total_pages);
            else if (data.total && data.limit) setTotalPages(Math.ceil(data.total / data.limit));
        }
        setLoading(false);
    }, [page, status_filter, direction_filter]);

    useEffect(() => {
        fetch_logs(page);
    }, [page, status_filter, direction_filter]);

    useEffect(() => {
        poll_ref.current = setInterval(() => fetch_logs(page), 15000);
        return () => clearInterval(poll_ref.current);
    }, [fetch_logs, page]);

    return (
        <div>
            <div className={styles.filter_row}>
                <select
                    className={styles.filter_select}
                    value={status_filter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                    <option value="">All Statuses</option>
                    <option value="completed">Completed</option>
                    <option value="in-progress">In Progress</option>
                    <option value="busy">Busy</option>
                    <option value="failed">Failed</option>
                    <option value="no-answer">No Answer</option>
                </select>
                <select
                    className={styles.filter_select}
                    value={direction_filter}
                    onChange={(e) => { setDirectionFilter(e.target.value); setPage(1); }}
                >
                    <option value="">All Directions</option>
                    <option value="inbound">Inbound</option>
                    <option value="outbound-dial">Outbound</option>
                </select>
                <button className={styles.refresh_btn} onClick={() => fetch_logs(page)}>
                    Refresh
                </button>
            </div>

            {loading ? (
                <div className={styles.loading_text}>Loading…</div>
            ) : logs.length === 0 ? (
                <div className={styles.empty_text}>No call logs found.</div>
            ) : (
                <table className={styles.logs_table}>
                    <thead>
                        <tr>
                            <th>Direction</th>
                            <th>From</th>
                            <th>To</th>
                            <th>Status</th>
                            <th>Duration</th>
                            <th>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log, i) => (
                            <tr key={log.sid || log.call_sid || i}>
                                <td>
                                    <span className={log.direction?.includes('inbound') ? styles.direction_in : styles.direction_out}>
                                        {log.direction?.includes('inbound') ? '↙ In' : '↗ Out'}
                                    </span>
                                </td>
                                <td>{log.from || log.from_formatted || '—'}</td>
                                <td>{log.to || log.to_formatted || '—'}</td>
                                <td>
                                    <span className={`${styles.badge} ${call_badge(log.status)}`}>
                                        {log.status || '—'}
                                    </span>
                                </td>
                                <td>{fmt_duration(log.duration)}</td>
                                <td>{fmt_date(log.start_time || log.date_created)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {total_pages > 1 && (
                <div className={styles.pagination_row}>
                    <span className={styles.page_info}>Page {page} of {total_pages}</span>
                    <div className={styles.pagination_btns}>
                        <button
                            className={styles.btn_page}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            ← Prev
                        </button>
                        <button
                            className={styles.btn_page}
                            onClick={() => setPage((p) => Math.min(total_pages, p + 1))}
                            disabled={page === total_pages}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Recordings ────────────────────────────────────────────────────────────

function Recordings() {
    const [recordings, setRecordings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total_pages, setTotalPages] = useState(1);
    const [deleting, setDeleting] = useState(null);

    const fetch_recordings = useCallback(async (p = page) => {
        setLoading(true);
        const result = await getRecordingsAPI({ page: p, limit: PAGE_LIMIT });
        const data = unwrap(result);

        if (data) {
            const items = Array.isArray(data) ? data : (data.recordings || data.data || data.items || []);
            setRecordings(items);
            if (data.total_pages) setTotalPages(data.total_pages);
            else if (data.total && data.limit) setTotalPages(Math.ceil(data.total / data.limit));
        }
        setLoading(false);
    }, [page]);

    useEffect(() => {
        fetch_recordings(page);
    }, [page]);

    const handle_delete = useCallback(async (sid) => {
        if (!window.confirm('Delete this recording?')) return;
        setDeleting(sid);
        await deleteRecordingAPI(sid);
        setDeleting(null);
        fetch_recordings(page);
    }, [fetch_recordings, page]);

    return (
        <div>
            <div className={styles.filter_row}>
                <button className={styles.refresh_btn} onClick={() => fetch_recordings(page)}>
                    Refresh
                </button>
            </div>

            {loading ? (
                <div className={styles.loading_text}>Loading…</div>
            ) : recordings.length === 0 ? (
                <div className={styles.empty_text}>No recordings found.</div>
            ) : (
                <div className={styles.rec_list}>
                    {recordings.map((rec) => (
                        <div key={rec.sid || rec.recording_sid} className={styles.rec_card}>
                            <div className={styles.rec_meta}>
                                <span className={styles.rec_sid}>
                                    {rec.sid || rec.recording_sid}
                                </span>
                                <span className={styles.rec_duration}>
                                    {fmt_duration(rec.duration)}
                                </span>
                                <span className={styles.rec_sid}>
                                    {fmt_date(rec.date_created || rec.created_at)}
                                </span>
                            </div>
                            {(rec.media_url || rec.url) && (
                                <audio
                                    className={styles.rec_audio}
                                    controls
                                    src={rec.media_url || rec.url}
                                    preload="none"
                                />
                            )}
                            <div className={styles.rec_actions}>
                                <button
                                    className={styles.btn_delete}
                                    onClick={() => handle_delete(rec.sid || rec.recording_sid)}
                                    disabled={deleting === (rec.sid || rec.recording_sid)}
                                >
                                    {deleting === (rec.sid || rec.recording_sid) ? 'Deleting…' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {total_pages > 1 && (
                <div className={styles.pagination_row}>
                    <span className={styles.page_info}>Page {page} of {total_pages}</span>
                    <div className={styles.pagination_btns}>
                        <button
                            className={styles.btn_page}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            ← Prev
                        </button>
                        <button
                            className={styles.btn_page}
                            onClick={() => setPage((p) => Math.min(total_pages, p + 1))}
                            disabled={page === total_pages}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Root Page ─────────────────────────────────────────────────────────────

const TABS = [
    { id: 'logs', label: 'Call History' },
    { id: 'recordings', label: 'Recordings' },
];

function TwilioPhone() {
    const voice = useTwilioVoice(AGENT_IDENTITY);
    const [active_tab, setActiveTab] = useState('logs');

    return (
        <div className={styles.page}>
            <h1 className={styles.page_title}>Twilio Phone</h1>

            <div className={styles.grid}>
                <PhonePanel voice={voice} />

                <div className={styles.right_panel}>
                    <div className={styles.tabs}>
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                className={`${styles.tab_btn} ${active_tab === tab.id ? styles.tab_btn_active : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tab_content}>
                        {active_tab === 'logs' && <CallLogs />}
                        {active_tab === 'recordings' && <Recordings />}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TwilioPhone;
